import React, { useState, useEffect, useRef } from 'react';  
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';  
import { faPaperPlane, faImage, faXmark, faStar, faDownload, faCheck, faCheckDouble } from '@fortawesome/free-solid-svg-icons'; 
import ConnectionStatus from "./ConnectionStatus";
import generatePDF from 'react-to-pdf';
import Resizer from "react-image-file-resizer";
import socket from "../services/socket";
import { v4 as uuidv4 } from "uuid";
import axios from "axios"; 

const Remote = () => {
  
  // State management
  const [inputValue, setInputValue] = useState("");  
  const [messages, setMessages] = useState([]); 
  const [clientId, setClientId] = useState("");
  const [agent, setAgent] = useState("Silulumanzi Support");  
  const [closed, setClosed] = useState(false); 
  const [file, setFile] = useState(null); 
  const [preview, setPreview] = useState(false);
  const [rated, setRated] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);  
  const [customerName, setCustomerName] = useState(""); 
  const [isTyping, setIsTyping] = useState(false);  
  const [staticId] = useState(uuidv4());
  const [isConnected, setIsConnected] = useState(false);
  const [showNameForm, setShowNameForm] = useState(true);
  const [nameInput, setNameInput] = useState("");
  
  const targetRef = useRef();
  const inputFile = useRef(null);  
  const messagesEndRef = useRef(null);
  const hasGreetedRef = useRef(false);
  // Guard to prevent duplicate sends (rapid double-submit)
  const sendingRef = useRef(false);
  // Track sent message ids to avoid re-sending same message
  const sentIdsRef = useRef(new Set());

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    // Method 1: Scroll the container to the bottom
    if (targetRef.current) {
      targetRef.current.scrollTop = targetRef.current.scrollHeight;
    }
    
    // Method 2: Also use scrollIntoView for the end marker
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat - but don't connect until name is provided
  useEffect(() => {
    // Get channel from URL
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const channel = params.get('channel');
    
    if (!channel) {
      setError("No channel specified");
      return;
    }
    
    // Check if name is already saved
    const savedName = window.sessionStorage.getItem("remote_chat_name");
    if (savedName) {
      setNameInput(savedName);
      setCustomerName(savedName);
      setShowNameForm(false);
      initializeChat(channel, savedName);
    } else {
      // Use channel as client ID even before name is provided
      setClientId(channel);
    }
    
    return () => {
      socket.off("receive-message");
      socket.off("connect_error");
      socket.off("connect");
      socket.off("disconnect");
    };
  }, []);

  // Handle name form submission
  const handleNameSubmit = (e) => {
    e.preventDefault();
    
    if (!nameInput.trim()) {
      alert("Please enter your name");
      return;
    }
    
    // Save name
    window.sessionStorage.setItem("remote_chat_name", nameInput.trim());
    setCustomerName(nameInput.trim());
    setShowNameForm(false);
    
    // Get channel and initialize chat
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const channel = params.get('channel');
    
    if (channel) {
      initializeChat(channel, nameInput.trim());
    }
  };

  // Initialize chat data and socket connection
  const initializeChat = async (channel, name) => {    
    console.log("🔍 Remote Chat: Initialize with channel:", channel, "name:", name);
    
    setClientId(channel);
    setIsLoading(true);

    setClientId(channel);
    setIsLoading(true);

    // Setup socket event listeners
    setupSocketListeners(channel, name);

    // Add greeting message only once
    if (!hasGreetedRef.current && name) {
      setTimeout(() => {
        addGreetingMessage(name);
        hasGreetedRef.current = true;
      }, 500);
    }
  };

  // Setup all socket event listeners
  const setupSocketListeners = (finalClientId, finalName) => {
    socket.on("receive-message", handleReceiveMessage);
    socket.on("connect", () => handleSocketConnect(finalClientId, finalName));
    socket.on("disconnect", handleSocketDisconnect);
    socket.on("connect_error", handleConnectionError);

    // If already connected, join immediately
    if (socket.connected) {
      handleSocketConnect(finalClientId, finalName);
    }
  };

  // Handle receiving messages from server
  const handleReceiveMessage = (new_message) => {   
    console.log("📨 Customer Portal: Received message:", new_message);
    
    // Only add message if it's not from us (avoid duplicate)
    const isFromAgent = new_message.direction === 1;
    const isFromOther = new_message.userId !== clientId && new_message.userId !== staticId;
    // Deduplicate: avoid adding messages that already exist (match by id or by timestamp+userId+message)
    let appended = false;
    setMessages(prev => {
      const alreadyExists = prev.some(m => {
        if (new_message.id && m.id && String(m.id) === String(new_message.id)) return true;
        if (m.timestamp === new_message.timestamp && String(m.userId) === String(new_message.userId) && String(m.message) === String(new_message.message)) return true;
        return false;
      });

      if (alreadyExists) {
        console.log("⏭️ Customer Portal: Skipping duplicate received message", new_message.id || new_message.timestamp);
        return prev;
      }

      if (isFromAgent || isFromOther) {
        appended = true;
        return [...prev, {
          ...new_message,
          isOwn: false,
          status: 'received'
        }];
      }

      console.log("⏭️ Customer Portal: Skipping own message (already added optimistically)");
      return prev;
    });

    if (appended) {
      socket.emit("read-message", new_message);
      // Scroll after a short delay to ensure DOM has updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  // Handle socket connection
  const handleSocketConnect = (finalClientId, finalName) => {
    console.log("🔌 Customer Portal: Socket connected, joining chat...");
    setIsConnected(true);
    
    const joinData = { 
      clientId: finalClientId, 
      userName: finalName, 
      department: 0, 
      direction: 0, 
      channel: finalClientId, 
      client: 'portal'
    };
    
    console.log("🚀 Customer Portal: Sending join with data:", joinData);
    
    socket.emit("join", joinData, (response) => {
      if (response && response.success) {
        console.log("✅ Customer Portal: Successfully joined chat");
        setError("");
        setIsLoading(false);
      } else {
        console.error("❌ Customer Portal: Failed to join chat:", response);
        setError("Failed to join chat session");
        setIsLoading(false);
      }
    });
  };

  // Handle socket disconnection
  const handleSocketDisconnect = (reason) => {
    console.log("🔌 Customer Portal: Disconnected:", reason);
    setIsConnected(false);
  };

  // Handle connection errors
  const handleConnectionError = (err) => {
    console.error("❌ Customer Portal: Connection error:", err);
    setError("Unable to connect to chat server. Please check your connection.");
    setIsLoading(false);
    setIsConnected(false);
  };

  // Add greeting message from bot
  const addGreetingMessage = (name) => {
    const greetingMessage = {
      timestamp: Math.floor(Date.now() / 1000), 
      name: "Silulumanzi",
      userName: "Silulumanzi",
      message: `Hi ${name}, how can we help you today?`,
      direction: 1,
      isOwn: false,
      status: 'received'
    };
    
    setMessages(prev => [...prev, greetingMessage]); 
  };

  // Send text message
  const sendMessage = async (e) => {
    e.preventDefault();  
    
    if (!inputValue.trim() && !preview) return; 

    // Prevent duplicate send calls (e.g. double Enter or double-click)
    if (sendingRef.current) {
      console.log("⏭️ Customer Portal: sendMessage blocked - already sending");
      return;
    }
    sendingRef.current = true;
    
    const messageText = inputValue.trim();
    const messageToSend = preview ? file : messageText;
    
    setInputValue('');
    setPreview(false);
    setFile(null);
    
    const timestamp = Math.floor(Date.now() / 1000);
    const currentClientId = clientId || staticId;

    const message = { 
      userId: currentClientId,
      name: customerName,
      userName: customerName,
      timestamp: timestamp,
      message: messageToSend,
      channelId: currentClientId,
      channel_id: currentClientId,
      department: 0,
      direction: 0,
      category: 1,
      id: `${currentClientId}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
      client: "portal",
      state: "Sent"
    }; 
    
    // Deduplicate by message id in case this function is somehow invoked twice
    if (sentIdsRef.current.has(message.id)) {
      console.log("⏭️ Customer Portal: Message already sent (dedup by id)", message.id);
      sendingRef.current = false;
      return;
    }
    sentIdsRef.current.add(message.id);

    console.log("📤 Customer Portal: Sending message:", message);
    
    // Add to local state immediately (optimistic update)
    setMessages(prev => [...prev, {
      ...message,
      isOwn: true,
      status: 'sending'
    }]);
    
    scrollToBottom();

    // Send via socket
    socket.emit("send-message", message);
    
    // Update status to sent after a delay
    setTimeout(() => {
      setMessages(prev => prev.map(msg => 
        msg.id === message.id ? { ...msg, status: 'sent' } : msg
      ));
      // allow sending again after status update
      sendingRef.current = false;
    }, 500);
  };

  // Handle file selection
  const handleFile = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const validExtensions = ['png', 'jpeg', 'jpg', 'gif', 'webp'];
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      alert("Please select a valid image file (PNG, JPEG, JPG, GIF, WEBP)");
      return;
    }

    try {
      Resizer.imageFileResizer(
        selectedFile,
        800,
        800,
        "JPEG",
        90,
        0,
        (uri) => { 
          setFile(uri); 
          setPreview(true);  
        },
        "base64",
        600,
        600
      );
    } catch (err) {
      console.error("Error resizing image:", err);
      alert("Error processing image. Please try another file.");
    }
  };

  // Trigger file input click
  const onFileClick = () => { 
    inputFile.current?.click();
  };

  // Cancel file preview
  const cancelPreview = () => {
    setPreview(false);
    setFile(null);
    setInputValue("");
  };

  // Close chat and show rating
  const closeChat = () => {
    setClosed(true);
  };

  // Submit rating
  const submitRating = async (rating) => { 
    const formData = new FormData();
    formData.append('Rating', rating);
    formData.append('Staff', agent);
    formData.append('Name', customerName); 
    
    try {
      const response = await axios.post(process.env.REACT_APP_API_BASE_URL + "chats/rating/", formData);
      if (response.data?.status === 1) {
        setRated(true); 
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
    }
  };

  // Download chat transcript
  const downloadTranscript = () => {
    generatePDF(targetRef, { filename: `chat-transcript-${Date.now()}.pdf` }); 
  };

  // Format timestamp
  const formatTime = (timestamp) => {     
    const date = new Date(timestamp * 1000);
    const today = new Date();
    const yesterday = new Date(Date.now() - 86400000);
    
    const hours = date.getHours().toString().padStart(2, '0');
    const mins = date.getMinutes().toString().padStart(2, '0');
    const time = `${hours}:${mins}`;
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${time}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${time}`;
    } else {
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      return `${day} ${month}, ${time}`;
    }
  };

  // Render message status icon
  const renderMessageStatus = (status) => {
    if (status === 'sending') {
      return <FontAwesomeIcon icon={faCheck} className="text-gray-400 text-xs ml-1" />;
    } else if (status === 'sent') {
      return <FontAwesomeIcon icon={faCheckDouble} className="text-blue-400 text-xs ml-1" />;
    } else if (status === 'received') {
      return <FontAwesomeIcon icon={faCheckDouble} className="text-blue-600 text-xs ml-1" />;
    }
    return null;
  };

  // Check if message contains base64 image
  const isBase64Image = (msg) => {
    return typeof msg === 'string' && msg.includes("base64");
  };

  // Check if message is a file download link
  const isFileDownload = (msg) => {
    const date = new Date();
    const filePattern = `File-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    return typeof msg === 'string' && msg.includes(filePattern);
  };

  return ( 
    <div className="chat-container" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      backgroundColor: '#f0f2f5',
      overflow: 'hidden'
    }}>
      {showNameForm ? (
        /* Name Collection Form */
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '40px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxWidth: '400px',
            width: '100%'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <img src="/images/favicon.png" alt="Support" style={{ width: '60px', height: '60px', marginBottom: '20px' }} />
              <h2 style={{ color: '#075e54', marginBottom: '10px' }}>Chat with Support</h2>
              <p style={{ color: '#667781' }}>Please enter your name to start the conversation</p>
            </div>
            
            <form onSubmit={handleNameSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: '#075e54',
                  fontWeight: '500'
                }}>
                  Your Name
                </label>
                <input 
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Enter your name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#075e54'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  autoFocus
                  required
                />
              </div>
              
              <button 
                type="submit"
                style={{
                  width: '100%',
                  backgroundColor: '#075e54',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#064e47'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#075e54'}
              >
                Start Chat
              </button>
            </form>
          </div>
        </div>
      ) : (
        /* Main Chat Interface */
        <>
      {/* Chat Header */}
      <div style={{
        backgroundColor: '#075e54',
        color: 'white',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <img src="/images/favicon.png" alt="Support" style={{ width: '30px', height: '30px' }} /> 
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '16px' }}>{agent}</div>
            <ConnectionStatus />
          </div>
        </div>
        {!closed && (
        <button 
          onClick={closeChat}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '4px 8px'
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        )}
      </div>

      {/* Messages Area */}
      <div 
        ref={targetRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          backgroundImage: 'url(/images/chat-bg.png)',
          backgroundSize: 'contain'
        }}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '10px' }}>Connecting to support...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isOwn = msg.isOwn || msg.direction === 0;
              const isImage = isBase64Image(msg.message);
              const isFile = isFileDownload(msg.message);
              
              return (
                <div 
                  key={msg.id || index}
                  style={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    marginBottom: '12px'
                  }}
                >
                  {/* Agent Avatar */}
                  {!isOwn && (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#dfe5e7',
                      marginRight: '8px',
                      flexShrink: 0,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img src="/images/favicon.png" alt="Agent" style={{ width: '24px', height: '24px' }} />
                    </div>
                  )}

                  {/* Message Bubble */}
                  <div style={{
                    maxWidth: '65%',
                    backgroundColor: isOwn ? '#dcf8c6' : 'white',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    position: 'relative'
                  }}>
                    {/* Sender Name (for received messages) */}
                    {!isOwn && msg.userName && (
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#075e54',
                        marginBottom: '4px'
                      }}>
                        {msg.userName}
                      </div>
                    )}

                    {/* Message Content */}
                    {isImage ? (
                      <img 
                        src={msg.message} 
                        alt="Shared image"
                        style={{ 
                          maxWidth: '100%', 
                          borderRadius: '4px',
                          display: 'block'
                        }} 
                      />
                    ) : isFile ? (
                      <a 
                        href={`${process.env.REACT_APP_API_BASE_URL}files/storage`}
                        download
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: '#f0f2f5',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          color: '#075e54'
                        }}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        <span>Download File</span>
                      </a>
                    ) : (
                      <div style={{ 
                        fontSize: '14px', 
                        lineHeight: '1.4',
                        wordWrap: 'break-word'
                      }}>
                        {msg.message}
                      </div>
                    )}

                    {/* Timestamp and Status */}
                    <div style={{
                      fontSize: '11px',
                      color: '#667781',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '4px'
                    }}>
                      <span>{formatTime(msg.timestamp)}</span>
                      {isOwn && renderMessageStatus(msg.status)}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div style={{
        backgroundColor: '#f0f2f5',
        padding: '10px 16px',
        borderTop: '1px solid #e0e0e0'
      }}>
        {preview ? (
          /* Image Preview */
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '10px',
            position: 'relative'
          }}>
            <button 
              onClick={cancelPreview}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'rgba(0,0,0,0.5)',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <img 
              src={file} 
              alt="Preview" 
              style={{ 
                maxWidth: '100%', 
                borderRadius: '4px',
                display: 'block',
                marginBottom: '12px'
              }} 
            />
            <button 
              onClick={sendMessage}
              style={{
                width: '100%',
                backgroundColor: '#075e54',
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                padding: '10px 20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>Send Image</span>
            </button>
          </div>
        ) : closed ? (
          /* Rating Area */
          rated ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <h6 style={{ marginBottom: '16px', color: '#075e54' }}>Thank you for your feedback!</h6>
              <p style={{ marginBottom: '16px', color: '#667781' }}>Would you like a copy of this chat?</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={closeChat}
                >
                  No, thanks
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={downloadTranscript}
                  style={{ backgroundColor: '#075e54', borderColor: '#075e54' }}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <h6 style={{ marginBottom: '16px', color: '#075e54' }}>How was your experience?</h6>
              <div style={{ fontSize: '32px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                {['Dissatisfied', 'Partially Satisfied', 'Average', 'Satisfied', 'Very Satisfied'].map((rating, idx) => (
                  <FontAwesomeIcon 
                    key={idx}
                    icon={faStar} 
                    onClick={() => submitRating(rating)}
                    style={{ 
                      color: '#ffd700',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  />
                ))}
              </div>
            </div>
          )
        ) : null}

        {/* Message Input */}
        {!closed && !preview && (
          <form onSubmit={sendMessage} style={{ display: 'flex', gap: '8px' }}>
            <input 
              type="file" 
              ref={inputFile} 
              style={{ display: 'none' }} 
              onChange={handleFile}
              accept="image/*"
            /> 

            <textarea 
              className="form-control" 
              value={inputValue} 
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder="Type a message..."
              rows="1"
              style={{
                flex: 1,
                borderRadius: '24px',
                padding: '10px 16px',
                resize: 'none',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            />

            <button 
              type="button"
              onClick={onFileClick}
              style={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '50%',
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#667781'
              }}
            >
              <FontAwesomeIcon icon={faImage} />
            </button>

            <button 
              type="submit"
              disabled={!inputValue.trim()}
              style={{
                backgroundColor: inputValue.trim() ? '#075e54' : '#ccc',
                border: 'none',
                borderRadius: '50%',
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                color: 'white',
                transition: 'background-color 0.2s'
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </form>
        )}
      </div>
      </>
      )}
    </div>
  ); 
}

export default Remote;
